import {
  EnumOperatorEnrollmentsOnboardingStatus,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  OperatorsByUserDocument,
  useOperatorUpdateMutation,
  UserFindOneDocument,
  useUserUpdateMutation,
} from "gql/generated";
import * as Sentry from "@sentry/react";
import { useAppContext } from "AppContext";

export const useClaimAnOperator = (_entityId: string) => {
  const { dbUser, operators } = useAppContext();

  const currentOperator = operators.find((op) => op._entityId === _entityId);

  const [updateOperator, { loading }] = useOperatorUpdateMutation({
    refetchQueries: [OperatorsByUserDocument],
    awaitRefetchQueries: true,
  });
  const [updateUser] = useUserUpdateMutation({
    refetchQueries: [UserFindOneDocument],
  });

  const handleUpdateUserToOwner = async () => {
    try {
      const operatorAccess = dbUser?.access.operators.find(
        (v) => v?.entityId === _entityId,
      );

      if (operatorAccess) {
        if (operatorAccess?.role !== EnumUserAccessOperatorsRole.Owner) {
          await updateUser({
            variables: {
              id: dbUser?._id,
              record: {
                access: {
                  ...dbUser?.access,
                  operators: dbUser?.access.operators.map((v) => {
                    return v?.entityId === _entityId
                      ? {
                          ...v,
                          role: EnumUserAccessOperatorsRole.Owner,
                          status: EnumUserAccessOperatorsStatus.Accepted,
                        }
                      : v;
                  }),
                },
              },
            },
          });
        }
      } else {
        // If operator doesn't exist on this user's access yet, add that
        // operator's access to the user
        const newOperatorAccesses = [
          ...(dbUser?.access.operators ?? []),
          {
            entityId: _entityId,
            role: EnumUserAccessOperatorsRole.Owner,
            status: EnumUserAccessOperatorsStatus.Accepted,
          },
        ];
        await updateUser({
          variables: {
            id: dbUser?._id,
            record: {
              access: {
                ...dbUser?.access,
                operators: newOperatorAccesses,
              },
            },
          },
        });
      }
    } catch (err) {
      /**
       * Catch any error here so it doesn't trickle back to the caller.
       * We don't want this error to be displayed to the user because the
       * operator will still be claimed regardless of an error at this stage
       * */
      console.error(err);
      Sentry.captureException(err);
    }
  };

  const handleClaimOperator = async () => {
    /**
     * Set all enrollments onboarding status to complete when claiming a
     * business. In the future we can redirect the user to a survey if
     * needed here.
     */
    const enrollments =
      currentOperator &&
      currentOperator.enrollments &&
      currentOperator.enrollments.some(
        (e) =>
          e?.onboardingStatus !==
          EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
      )
        ? {
            enrollments: [...currentOperator.enrollments].map((e) => ({
              ...e,
              onboardingStatus:
                EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
            })),
          }
        : {};

    // Do not catch errors, allow errors to trickle back to the caller
    await updateOperator({
      variables: {
        entityId: _entityId,
        record: { claimed: true, ...enrollments },
      },
    });

    handleUpdateUserToOwner();
  };

  return { onClaimOperator: handleClaimOperator, loading };
};
